import { computed, makeObservable } from 'mobx';

import { LoginErrors } from 'shared/entities/user';
import {
  MAX_NAME_LENGTH,
  validateEmailField,
  validateIsEmpty,
  validatePassword,
  validateTextFieldMaxLength,
  ValidatorResult
} from 'shared/entities/validator';
import { FieldModel } from 'shared/models/form';

export default class UserEditModel {
  readonly email: FieldModel<string>;
  readonly password: FieldModel = new FieldModel<string>('', [
    validateIsEmpty,
    validatePassword
  ]);

  readonly emailTakenError = new FieldModel<ValidatorResult>(null);

  constructor() {
    this.email = new FieldModel<string>('', [
      validateIsEmpty,
      validateEmailField,
      (value) => validateTextFieldMaxLength(value, MAX_NAME_LENGTH)
    ]);

    makeObservable(this, {
      isError: computed
    });
  }

  get emailError(): ValidatorResult {
    return this.emailTakenError.value || this.email.error;
  }

  get isError(): boolean {
    return !!this.emailError || this.password.isError;
  }

  reset(): void {
    this.email.reset();
    this.password.reset();
    this.resetErrors();
  }

  resetErrors(): void {
    this.email.resetError();
    this.emailTakenError.changeValue(null);
    this.password.resetError();
  }

  validate(): void {
    this.email.validate();
    this.password.validate();
  }

  private get hasPairedLoginError(): boolean {
    return this.email.error === LoginErrors.INVALID_EMAIL_OR_PASSWORD;
  }

  clearPairedLoginError = (): void => {
    if (!this.hasPairedLoginError) {
      return;
    }

    this.email.resetError();
    this.password.resetError();
  };

  validateEmail = () => {
    if (!this.emailTakenError.value) {
      this.email.validate();
    }
  };

  changeEmail = (value: string) => {
    if (this.email.value !== value) {
      this.clearPairedLoginError();
    }

    this.email.changeValue(value);
    this.emailTakenError.changeValue(null);
  };

  changePassword = (value: string): void => {
    if (this.password.value === value) {
      return;
    }

    this.clearPairedLoginError();
    this.password.changeValue(value);
  };

  toJson(): {
    email: string;
    password: string;
  } {
    return {
      email: this.email.value.trim(),
      password: this.password.value.trim()
    };
  }
}
